import { applyActionParams } from "@/action/actions";
import { boardActions } from "@/action/boardActions";
import { navigationActions } from "@/action/navigationActions";
import { toggleActions } from "@/action/toggleActions";
import type { Action } from "@/action/types";
import { isSessionAlmSynced } from "@/backend/read/BackendReader";
import { relativeClientCoord } from "@/math/coordinate-utils";
import type { RelativeCoordinate } from "@/model/coordinates";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useConnectionStore } from "@/store/connection";
import { useStickyTypeStore } from "@/store/stickyType";
import { useUserStore } from "@/store/user";

import { BaseBoard } from "./BaseBoard";
import { TeamBoardLocation } from "./location/TeamBoardLocation";

export class TeamBoard extends BaseBoard<"team"> {
  override location(pos: RelativeCoordinate | number | string) {
    return TeamBoardLocation.of(this.board.iterationStates, pos);
  }

  override positionalCardProperties(pos: RelativeCoordinate) {
    const loc = this.location(pos);
    return { iterationId: loc.iterationId, teamId: this.board.team.id };
  }

  override get showPermanentLinks() {
    return useClientSettingsStore().permanentTeamRiskCanvasLinks;
  }

  override get showRiskyLinks() {
    return true;
  }

  get possibleStickyTypes() {
    return useStickyTypeStore().nonFlexBoardStickyTypes("team");
  }

  get contextActions() {
    return {
      board: { draw: this.drawAction() },
      selection: { organize: true, link: true },
    };
  }

  override positionalActions(c: RelativeCoordinate) {
    const loc = this.location(c);
    return !loc.isIteration && !loc.isRisks()
      ? {}
      : {
          region: {
            name: loc.name,
            organize: true,
            actions: {
              overview: this.overviewAction(loc),
              sync: this.syncAction(loc),
              zoom: this.zoomAction(loc),
              kanban: this.showKanbanBoard(loc),
              myWork: this.myWork(loc),
            },
          },
        };
  }

  myWork(loc: TeamBoardLocation): Action | undefined {
    const iterationId = loc.iterationId;
    if (loc.isIteration && iterationId !== null) {
      return applyActionParams(navigationActions.showMyWork, { iterationId });
    }
  }

  showKanbanBoard(loc: TeamBoardLocation): Action | undefined {
    const iterationId = loc.iterationId;
    if (loc.isIteration && iterationId !== null) {
      return applyActionParams(navigationActions.showKanban, { iterationId });
    }
    return undefined;
  }

  syncAction(loc: TeamBoardLocation): Action | undefined {
    if (
      useUserStore().isAllowed("edit") &&
      useConnectionStore().alm &&
      isSessionAlmSynced() &&
      !!loc.boardIteration &&
      loc.iterationState?.status !== "syncing"
    ) {
      return applyActionParams(
        boardActions.confirmSyncIteration,
        loc.iterationId,
        loc.iterationState?.status ?? null,
      );
    }
  }

  overview(e: MouseEvent) {
    this.overviewAction(this.location(relativeClientCoord(e)))?.("mouse");
  }

  overviewAction(loc: TeamBoardLocation): Action | undefined {
    if (loc.isObjectives()) {
      return;
    }
    return applyActionParams(toggleActions.showOverview, {
      load: loc.boardIteration(this.board)?.load,
      velocity: loc.boardIteration(this.board)?.velocity,
      boardId: this.board.id,
      location: loc.id(),
    });
  }
}
